import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import OptimizedImage from '@/components/OptimizedImage';
import { API } from '@/App';
import axios from 'axios';
import { Linkedin, Twitter, Github, Mail, Globe, Sparkles } from 'lucide-react';

const TeamPage = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  // "ai-" ön ekli kayıtlar ekip üyesi değil, kullanılan yapay zeka teknolojileridir
  const isAiTech = (m) => String(m.id || '').startsWith('ai-');
  const humans = team.filter((m) => !isAiTech(m));
  const aiTech = team.filter(isAiTech);

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API}/team?active_only=true`);
      setTeam(response.data);
    } catch (error) {
      console.error('Error fetching team:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Helmet>
        <title>Ekibimiz - Ener Yazılım | Uzman Kadromuz</title>
        <meta name="description" content="Uzman kadromuzla projelerinize değer katıyoruz. Deneyimli yazılım geliştiriciler, siber güvenlik uzmanları ve teknoloji profesyonelleri." />
        <meta name="keywords" content="ekip, uzman kadro, yazılım ekibi, teknoloji uzmanları, ener yazılım ekibi" />
        <link rel="canonical" href="https://www.eneryazilim.com/ekip" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.eneryazilim.com/ekip" />
        <meta property="og:title" content="Ekibimiz - Ener Yazılım" />
        <meta property="og:description" content="Uzman kadromuzla projelerinize değer katıyoruz." />
        <meta property="og:image" content="https://www.eneryazilim.com/logo.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.eneryazilim.com/ekip" />
        <meta name="twitter:title" content="Ekibimiz - Ener Yazılım" />
        <meta name="twitter:description" content="Uzman kadromuzla projelerinize değer katıyoruz." />
        <meta name="twitter:image" content="https://www.eneryazilim.com/logo.png" />
        
        {/* Structured Data - Organization with Team */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Ener Yazılım",
            "url": "https://www.eneryazilim.com",
            "employee": humans.map(member => ({
              "@type": "Person",
              "name": member.name,
              "jobTitle": member.position,
              "description": member.bio
            }))
          })}
        </script>
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden" data-testid="team-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/10 via-transparent to-[#F7931E]/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="team-title">
              <span className="gradient-text">Ekibimiz</span>
            </h1>
            <p className="text-lg text-white/70">
              Uzman kadromuzla projelerinize değer katıyoruz
            </p>
          </div>
        </div>
      </section>

      {/* Team Members */}
      <section className="pb-20" data-testid="team-members">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center text-white/60">Yükleniyor...</div>
          ) : humans.length === 0 ? (
            <div className="text-center text-white/60" data-testid="no-team">Henüz ekip üyesi eklenmemiş.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {humans.map((member, index) => (
                <div
                  key={member.id}
                  data-testid={`team-member-${member.id}`}
                  className="glass rounded-2xl overflow-hidden card-hover"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {member.image_url ? (
                    <OptimizedImage
                      src={member.image_url}
                      alt={member.name}
                      className="h-64"
                      loading="lazy"
                      objectFit="cover"
                    />
                  ) : (
                    <div className="h-64 bg-gradient-to-br from-[#FF6B35]/20 to-[#F7931E]/20 flex items-center justify-center">
                      <div className="text-6xl">👤</div>
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-1">{member.name}</h3>
                    <p className="text-sm text-[#FF6B35] mb-3">{member.position}</p>
                    <p className="text-sm text-white/60 mb-4">{member.bio}</p>
                    {member.social_links && Object.keys(member.social_links).length > 0 && (
                      <div className="flex space-x-2">
                        {member.social_links.linkedin && (
                          <a
                            href={member.social_links.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg bg-white/5 hover:bg-[#FF6B35] transition-colors"
                            data-testid={`member-linkedin-${member.id}`}
                          >
                            <Linkedin size={16} />
                          </a>
                        )}
                        {member.social_links.twitter && (
                          <a
                            href={member.social_links.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg bg-white/5 hover:bg-[#FF6B35] transition-colors"
                            data-testid={`member-twitter-${member.id}`}
                          >
                            <Twitter size={16} />
                          </a>
                        )}
                        {member.social_links.github && (
                          <a
                            href={member.social_links.github}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg bg-white/5 hover:bg-[#FF6B35] transition-colors"
                            data-testid={`member-github-${member.id}`}
                          >
                            <Github size={16} />
                          </a>
                        )}
                        {member.social_links.email && (
                          <a
                            href={`mailto:${member.social_links.email}`}
                            className="p-2 rounded-lg bg-white/5 hover:bg-[#FF6B35] transition-colors"
                            data-testid={`member-email-${member.id}`}
                          >
                            <Mail size={16} />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Kullandığımız Yapay Zeka Teknolojileri */}
      {!loading && aiTech.length > 0 && (
        <section className="pb-20" data-testid="ai-tech">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Kullandığımız Yapay Zeka Teknolojileri</h2>
              <p className="text-white/60">
                Projelerimizde kullandığımız model ve araçlar. İhtiyaca göre bulutta ya da müşteri altyapısında, veri gizliliği gözetilerek çalıştırılır.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {aiTech.map((tech, index) => (
                <div
                  key={tech.id}
                  data-testid={`ai-tech-${tech.id}`}
                  className="glass rounded-2xl p-6 card-hover flex flex-col"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-lg bg-gradient-to-br from-[#FF6B35]/20 to-[#F7931E]/20">
                      <Sparkles className="w-5 h-5 text-[#FF6B35]" />
                    </div>
                    {tech.social_links?.website && (
                      <a
                        href={tech.social_links.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg bg-white/5 hover:bg-[#FF6B35] transition-colors"
                        aria-label={`${tech.name} web sitesi`}
                        data-testid={`ai-tech-link-${tech.id}`}
                      >
                        <Globe size={16} />
                      </a>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold mb-1">{tech.name}</h3>
                  <p className="text-sm text-[#FF6B35] mb-3">{tech.position}</p>
                  <p className="text-sm text-white/60">{tech.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default TeamPage;